import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Alert,
  Spinner,
  Modal,
  Form,
  Button,
} from "react-bootstrap";
import HeaderTwo from "../../components/HeaderTwo";
import { BreadcrumbBox } from "../../components/common/Breadcrumb";
import FooterTwo from "../../components/FooterTwo";
import { Styles, IntakeModalStyles } from "./styles/adminIntakes.js";
import {
  ADMIN_INTAKES_ENDPOINT,
  ADMIN_INTAKES_UPDATE_ENDPOINT,
} from "../../config/api";
import { useAuth } from "../../context/AuthContext";

const initialForm = {
  name: "",
  year: "",
  startDate: "",
  endDate: "",
  notes: "",
  status: "enabled",
};

const formatDateInput = (value) => {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toISOString().slice(0, 10);
};

const formatDisplayDate = (value) => {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return date.toLocaleDateString();
};

const formatStatus = (value) => {
  if (typeof value === "string" && value.toLowerCase() === "disabled") {
    return "Disabled";
  }
  return "Enabled";
};

const AdminIntakes = () => {
  const { user, loading: authLoading } = useAuth();

  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 1,
  });
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [yearInput, setYearInput] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [modalState, setModalState] = useState({
    show: false,
    mode: "add",
    target: null,
    form: { ...initialForm },
    saving: false,
    error: "",
  });
  const [deletingId, setDeletingId] = useState("");

  const isAdmin = useMemo(() => {
    if (!Array.isArray(user?.roles)) {
      return false;
    }
    return user.roles.some(
      (role) => typeof role === "string" && role.toLowerCase() === "admin"
    );
  }, [user]);

  const fetchIntakes = useCallback(async () => {
    if (!isAdmin) {
      return;
    }
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      const params = new URLSearchParams({
        page: String(page),
        limit: String(pageSize),
      });
      if (searchTerm.trim()) {
        params.set("search", searchTerm.trim());
      }
      if (yearFilter.trim()) {
        params.set("year", yearFilter.trim());
      }

      const response = await fetch(
        `${ADMIN_INTAKES_ENDPOINT}?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || "Unable to load intakes.");
      }

      const payload = await response.json();
      const results = Array.isArray(payload.intakes) ? payload.intakes : [];
      setIntakes(results);
      if (payload.pagination) {
        const serverPage = payload.pagination.page || page;
        setPagination({
          page: serverPage,
          limit: payload.pagination.limit || pageSize,
          totalItems: payload.pagination.totalItems || 0,
          totalPages: payload.pagination.totalPages || 1,
        });
        if (serverPage !== page) {
          setPage(serverPage);
        }
      } else {
        setPagination({
          page,
          limit: pageSize,
          totalItems: results.length,
          totalPages: 1,
        });
      }
    } catch (err) {
      setError(err.message || "Unable to load intakes.");
      setIntakes([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, page, pageSize, searchTerm, yearFilter]);

  useEffect(() => {
    if (!authLoading && isAdmin) {
      fetchIntakes();
    }
  }, [authLoading, isAdmin, fetchIntakes]);

  const openModal = (mode, target = null) => {
    if (mode === "edit" && target) {
      setModalState({
        show: true,
        mode: "edit",
        target,
        form: {
          name: target.name || "",
          year: target.year !== undefined ? String(target.year) : "",
          startDate: formatDateInput(target.startDate),
          endDate: formatDateInput(target.endDate),
          notes: target.notes || "",
          status: target.status ? target.status.toLowerCase() : "enabled",
        },
        saving: false,
        error: "",
      });
    } else {
      setModalState({
        show: true,
        mode: "add",
        target: null,
        form: { ...initialForm },
        saving: false,
        error: "",
      });
    }
  };

  const closeModal = () => {
    setModalState({
      show: false,
      mode: "add",
      target: null,
      form: { ...initialForm },
      saving: false,
      error: "",
    });
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setModalState((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        [name]: value,
      },
      error: "",
    }));
  };

  const submitModal = async (event) => {
    event.preventDefault();
    setModalState((prev) => ({ ...prev, saving: true, error: "" }));

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      const trimmedName = modalState.form.name.trim();
      const trimmedYear = modalState.form.year.trim();
      const trimmedStart = modalState.form.startDate;
      const trimmedEnd = modalState.form.endDate;

      if (!trimmedName || !trimmedYear || !trimmedStart || !trimmedEnd) {
        throw new Error("Name, year, start date, and end date are required.");
      }

      const parsedYear = Number(trimmedYear);
      if (!Number.isFinite(parsedYear)) {
        throw new Error("Year must be a valid number.");
      }

      const normalizedStatus = (modalState.form.status || "enabled")
        .toString()
        .trim()
        .toLowerCase();
      if (!["enabled", "disabled"].includes(normalizedStatus)) {
        throw new Error("Status must be either enabled or disabled.");
      }

      const payload = {
        name: trimmedName,
        year: parsedYear,
        startDate: trimmedStart,
        endDate: trimmedEnd,
        notes: modalState.form.notes.trim(),
        status: normalizedStatus,
      };

      const requestConfig = {
        method: modalState.mode === "edit" ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      };

      const endpoint =
        modalState.mode === "edit" && modalState.target?.id
          ? ADMIN_INTAKES_UPDATE_ENDPOINT(modalState.target.id)
          : ADMIN_INTAKES_ENDPOINT;

      const response = await fetch(endpoint, requestConfig);
      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result.message || "Unable to save intake.");
      }

      closeModal();
      fetchIntakes();
    } catch (err) {
      setModalState((prev) => ({
        ...prev,
        error: err.message || "Unable to save intake.",
      }));
    } finally {
      setModalState((prev) => ({ ...prev, saving: false }));
    }
  };

  const deleteIntake = async (id) => {
    if (!window.confirm("Are you sure you want to delete this intake?")) {
      return;
    }
    setDeletingId(id);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      const response = await fetch(ADMIN_INTAKES_UPDATE_ENDPOINT(id), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.message || "Unable to delete intake.");
      }
      fetchIntakes();
    } catch (err) {
      setError(err.message || "Unable to delete intake.");
    } finally {
      setDeletingId("");
    }
  };

  const resetFilters = () => {
    setSearchInput("");
    setSearchTerm("");
    setYearInput("");
    setYearFilter("");
    setPage(1);
  };

  const applyFilters = () => {
    setSearchTerm(searchInput.trim());
    setYearFilter(yearInput.trim());
    setPage(1);
  };

  return (
    <Styles>
      <IntakeModalStyles />
      <div className="main-wrapper admin-intakes-page">
        <HeaderTwo />
        <BreadcrumbBox title="Manage Intakes" />

        <section className="intakes-area">
          <Container>
            <Row>
              <Col md="12">
                {!authLoading && !isAdmin && (
                  <Alert variant="danger">
                    You do not have permission to view this page.
                  </Alert>
                )}
                {isAdmin && (
                  <div className="card-box">
                    <div className="actions">
                      <Button onClick={() => openModal("add")}>
                        Add Intake
                      </Button>
                      <div className="filters">
                        <input
                          type="text"
                          placeholder="Search intakes..."
                          value={searchInput}
                          onChange={(event) =>
                            setSearchInput(event.target.value)
                          }
                          disabled={loading}
                        />
                        <input
                          type="text"
                          placeholder="Year"
                          value={yearInput}
                          onChange={(event) => setYearInput(event.target.value)}
                          disabled={loading}
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={resetFilters}
                          disabled={loading}
                        >
                          Reset
                        </Button>
                        <Button
                          variant="success"
                          onClick={applyFilters}
                          disabled={loading}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>

                    {error && (
                      <Alert variant="danger" className="mt-3">
                        {error}
                      </Alert>
                    )}

                    {loading ? (
                      <div className="text-center my-4">
                        <Spinner animation="border" role="status" />
                      </div>
                    ) : (
                      <div className="data-grid">
                        <table>
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Year</th>
                              <th>Start</th>
                              <th>End</th>
                              <th>Status</th>
                              <th>Notes</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {intakes.length === 0 ? (
                              <tr>
                                <td colSpan="7" className="text-center muted">
                                  No intakes found.
                                </td>
                              </tr>
                            ) : (
                              intakes.map((intake) => (
                                <tr key={intake.id}>
                                  <td>{intake.name || "Untitled"}</td>
                                  <td>{intake.year ?? "—"}</td>
                                  <td>{formatDisplayDate(intake.startDate)}</td>
                                  <td>{formatDisplayDate(intake.endDate)}</td>
                                  <td>{formatStatus(intake.status)}</td>
                                  <td>{intake.notes || "—"}</td>
                                  <td>
                                    <div
                                      style={{ display: "flex", gap: "0.5rem" }}
                                    >
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() =>
                                          openModal("edit", intake)
                                        }
                                      >
                                        Edit
                                      </Button>
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => deleteIntake(intake.id)}
                                        disabled={deletingId === intake.id}
                                      >
                                        {deletingId === intake.id
                                          ? "Deleting..."
                                          : "Delete"}
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>

                        {intakes.length > 0 && (
                          <div className="pagination-controls">
                            <div className="page-info">
                              Page {pagination.page} of {pagination.totalPages}{" "}
                              • {pagination.totalItems} total
                            </div>
                            <div className="page-actions">
                              <Button
                                variant="outline-secondary"
                                onClick={() =>
                                  setPage((prev) => Math.max(prev - 1, 1))
                                }
                                disabled={page <= 1 || loading}
                              >
                                Previous
                              </Button>
                              <Button
                                variant="outline-secondary"
                                onClick={() =>
                                  setPage((prev) =>
                                    Math.min(
                                      prev + 1,
                                      pagination.totalPages || prev + 1
                                    )
                                  )
                                }
                                disabled={
                                  page >= (pagination.totalPages || 1) ||
                                  loading
                                }
                              >
                                Next
                              </Button>
                              <select
                                value={pageSize}
                                onChange={(event) => {
                                  setPageSize(Number(event.target.value));
                                  setPage(1);
                                }}
                                disabled={loading}
                              >
                                {[10, 20, 50, 100].map((size) => (
                                  <option key={size} value={size}>
                                    {size} / page
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Col>
            </Row>
          </Container>
        </section>

        <FooterTwo />
      </div>

      <Modal
        show={modalState.show}
        onHide={closeModal}
        centered
        dialogClassName="admin-intakes-modal"
        size="lg"
      >
        <Form onSubmit={submitModal}>
          <Modal.Header closeButton>
            <Modal.Title>
              {modalState.mode === "add" ? "Add Intake" : "Edit Intake"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalState.error && (
              <Alert variant="danger">{modalState.error}</Alert>
            )}
            <Row>
              <Col md="6">
                <Form.Group controlId="intake_name">
                  <Form.Label>Intake Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={modalState.form.name}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="intake_year">
                  <Form.Label>Intake Year</Form.Label>
                  <Form.Control
                    type="number"
                    name="year"
                    value={modalState.form.year}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md="6">
                <Form.Group controlId="intake_start">
                  <Form.Label>Intake Start</Form.Label>
                  <Form.Control
                    type="date"
                    name="startDate"
                    value={modalState.form.startDate}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="intake_end">
                  <Form.Label>Intake End</Form.Label>
                  <Form.Control
                    type="date"
                    name="endDate"
                    value={modalState.form.endDate}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md="6">
                <Form.Group controlId="intake_status">
                  <Form.Label>Status</Form.Label>
                  <Form.Control
                    as="select"
                    name="status"
                    value={modalState.form.status}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                  >
                    <option value="enabled">Enabled</option>
                    <option value="disabled">Disabled</option>
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md="12">
                <Form.Group controlId="intake_notes">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="notes"
                    value={modalState.form.notes}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                    placeholder="Optional internal notes about this intake window"
                  />
                  <div className="muted mt-1">
                    Intake notes are optional and only visible to
                    administrators.
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={closeModal}
              disabled={modalState.saving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={modalState.saving}
            >
              {modalState.saving ? "Saving..." : "Save Intake"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Styles>
  );
};

export default AdminIntakes;
